import React, { FC, useState } from 'react';

import styled from 'styled-components';
import { Link } from "react-router-dom";


const Container = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid #eee;
  border-radius: 8px;
  overflow: hidden;
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  @media only screen and (max-width: 1024px) {
    flex-direction: row;
    padding: 12px;
    margin: 0;
    border-radius: 12px;
    background: #fff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }
`;

const ImageContainer = styled.div`
  line-height: 1.8rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #F5F5F5;
  width: 100%;
  aspect-ratio: 1;
  overflow: hidden;
  position: relative;

  @media only screen and (max-width: 1024px) {
    padding: 0;
    background-color: #F5F5F5;
    flex: 0 0 80px;
    width: 80px;
    height: 80px;
    max-width: 80px;
    border-radius: 8px;
    overflow: hidden;
    margin-right: 12px;
    aspect-ratio: 1;
  }
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  position: absolute;
  top: 0;
  left: 0;

  @media only screen and (max-width: 1024px) {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const ImagePlaceholder = styled.div`
  width: 100%;
  height: 100%;
  background-color: #D1D5DB;
  border-radius: 8px;
`;

const TextContainer = styled.div`
  line-height: 1.8rem;
  display: flex;
  flex-direction: column;
  padding: 16px;
  flex: 1;
  justify-content: space-between;

  @media only screen and (max-width: 1024px) {
    padding: 0;
    flex: 1;
    justify-content: center;
  }
`;

const Name = styled.span`
  line-height: 3rem;
  font-size: 1rem;
  font-weight: 600;

  @media only screen and (max-width: 1024px) {
    line-height: 1.4;
    font-size: 0.95rem;
    margin-bottom: 4px;
  }
`;

const UserName = styled.span`
  color: #000;
  font-size: 0.75rem;
  font-weight: bold;
  line-height: 1.4;
  margin-bottom: 4px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;

  @media only screen and (max-width: 1024px) {
    font-size: 0.7rem;
    margin-bottom: 2px;
  }
`;

const CampaignStats = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.75rem;
  color: #9CA3AF;

  @media only screen and (max-width: 1024px) {
    font-size: 0.7rem;
    gap: 2px;
  }
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const StatIcon = styled.div`
  width: 10px;
  height: 10px;
  background-color: #9CA3AF;
  
  &.user-icon {
    mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='currentColor'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' /%3E%3C/svg%3E") no-repeat center;
    mask-size: contain;
  }
  
  &.task-icon {
    mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='currentColor'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' /%3E%3C/svg%3E") no-repeat center;
    mask-size: contain;
  }

  @media only screen and (max-width: 1024px) {
    width: 8px;
    height: 8px;
  }
`;

const Separator = styled.span`
  color: #9CA3AF;
  font-weight: normal;
`;

interface Props {
  [item: string]: CampaignItemType;
}

interface CampaignItemType {
  id: string,
  image_url: string,
  name: string,
  description: string,
  user: {
    first_name: string,
    last_name: string,
  },
  joined_user_count: number,
  task_count: number,
}

const CampaignItem: FC<Props> = ({ item, ...props }) => {
  const [hasImage, setHasImage] = useState(!!item.image_url);
  const [imageLoaded, setImageLoaded] = useState(false);

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const handleImageError = () => {
    setHasImage(false);
    setImageLoaded(false);
  };

  const showImage = hasImage && item.image_url;

  return (
    <Link to={`/campaign?id=${item.id}`}>
      <Container>
        <ImageContainer>
          {showImage ? (
            <Image
              src={item.image_url}
              alt={item.name}
              onLoad={handleImageLoad}
              onError={handleImageError}
              style={{ opacity: imageLoaded ? 1 : 0.7 }}
            />
          ) : (
            <ImagePlaceholder />
          )}
        </ImageContainer>

        <TextContainer className="text-container">
          <Name>{item.name}</Name>
          <UserName>{item.user?.first_name} {item.user?.last_name}</UserName>
          <CampaignStats>
            <StatItem>
              <StatIcon className="user-icon" />
              {item.joined_user_count || 0}
            </StatItem>
            <Separator>|</Separator>
            <StatItem>
              <StatIcon className="task-icon" />
              {item.task_count || 0}
            </StatItem>
          </CampaignStats>
        </TextContainer>
      </Container>
    </Link>
  );
}

export default CampaignItem;
